/**
 * Deterministic repairs.
 *
 * Each repair is a pure function per cell, and each one only touches cells it
 * is allowed to touch (dates are only rewritten inside date columns, currency
 * only inside numeric columns or values that carry a currency marker). Running
 * a repair twice always yields the same result — no heuristics, no randomness.
 */
import { isNullLike, parseDateValue } from './detect';
import type { ColumnSchema, ColumnType, RepairConfig, RepairKey, Row, RowValue } from './types';
import { REPAIR_ORDER } from './types';
import { dedupeHeaders, snakeCase } from './format';

export type RepairCounts = Record<RepairKey, number>;

export function zeroRepairCounts(): RepairCounts {
  return {
    trimWhitespace: 0,
    standardizeNull: 0,
    stripCurrency: 0,
    normalizeDates: 0,
    normalizeHeaders: 0,
  };
}

/** Removes BOM/zero-width noise, folds NBSP to a regular space, and trims. */
export function normalizeWhitespace(value: string): string {
  return value
    .replace(/\uFEFF/g, '')
    .replace(/[\u200B-\u200D\u2060\u180E]/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();
}

const CURRENCY_SYMBOLS = /[$€£¥₹₽₩]/;
const CURRENCY_CODES = /\b(?:USD|EUR|GBP|JPY|INR|CHF|CAD|AUD|CNY|NZD)\b/i;
const PLAIN_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
const EU_GROUPED = /^[+-]?\d{1,3}(?:\.\d{3})*,\d+$/; // 1.234,56
const US_GROUPED = /^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/; // 1,234.56
const SPACE_GROUPED = /^[+-]?\d{1,3}(?:\s\d{3})+(?:[.,]\d+)?$/; // 1 234.56 / 1 234,56

/**
 * Normalizes a currency string to a bare number, or returns null when the cell
 * is not a currency-formatted number.
 *
 * Guardrails: numeric columns are always eligible; text columns only when the
 * cell carries a currency symbol or ISO code, so "12 apples" is never mangled.
 */
export function stripCurrencyValue(raw: string, type: ColumnType): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const isNumericColumn = type === 'int' || type === 'float';
  const marked = CURRENCY_SYMBOLS.test(trimmed) || CURRENCY_CODES.test(trimmed);
  if (!isNumericColumn && !marked) return null;

  let value = trimmed
    .replace(CURRENCY_SYMBOLS, '')
    .replace(CURRENCY_CODES, '')
    .trim();

  if (EU_GROUPED.test(value)) value = value.replace(/\./g, '').replace(',', '.');
  else if (US_GROUPED.test(value)) value = value.replace(/,/g, '');
  else if (SPACE_GROUPED.test(value)) {
    value = value.replace(/\s/g, '');
    // Space-grouped with a comma decimal ("1 234,56") -> dot decimal.
    if (!value.includes('.') && value.includes(',')) value = value.replace(',', '.');
  }

  if (!PLAIN_NUMBER.test(value)) return null;
  return value === trimmed ? null : value; // only report cells that actually changed
}

export interface RepairOutcome {
  headers: string[];
  rows: Row[];
  counts: RepairCounts;
}

const ACTIVE_ORDER: RepairKey[] = REPAIR_ORDER.filter((k) => k !== 'normalizeHeaders');

/** Applies the enabled repairs to headers + rows, reporting how many cells changed. */
export function applyRepairs(
  headers: string[],
  rows: Row[],
  columns: ColumnSchema[],
  repairs: RepairConfig,
): RepairOutcome {
  const counts = zeroRepairCounts();
  const enabled = ACTIVE_ORDER.filter((key) => repairs[key]);

  const nextRows = rows.map((row) =>
    row.map((cell, columnIndex) => {
      let value: RowValue = cell;
      if (value === null || value === undefined) return null;
      const column = columns[columnIndex];
      const type = column?.type ?? 'string';

      for (const key of enabled) {
        if (value === null) return null;
        if (key === 'trimWhitespace') {
          const trimmed = normalizeWhitespace(value);
          if (trimmed !== value) {
            counts.trimWhitespace++;
            value = trimmed;
          }
        } else if (key === 'standardizeNull') {
          if (isNullLike(value)) {
            counts.standardizeNull++;
            return null;
          }
        } else if (key === 'stripCurrency') {
          const stripped = stripCurrencyValue(value, type);
          if (stripped !== null && stripped !== value) {
            counts.stripCurrency++;
            value = stripped;
          }
        } else if (key === 'normalizeDates') {
          if (type !== 'iso-date') continue;
          const parsed = parseDateValue(value);
          if (parsed && parsed.iso !== value) {
            counts.normalizeDates++;
            value = parsed.iso;
          }
        }
      }
      return value;
    }),
  );

  let nextHeaders = headers.slice();
  if (repairs.normalizeHeaders) {
    nextHeaders = dedupeHeaders(headers.map((header) => snakeCase(header, 'column')));
    counts.normalizeHeaders = headers.reduce((sum, header, i) => sum + (header === nextHeaders[i] ? 0 : 1), 0);
  }

  return { headers: nextHeaders, rows: nextRows, counts };
}

/**
 * Dry-run used by the UI to show "this repair would touch N cells" before the
 * user enables it. Each repair is measured independently of the others.
 */
export function countRepairHints(
  headers: string[],
  rows: Row[],
  columns: ColumnSchema[],
): RepairCounts {
  const hints = zeroRepairCounts();
  for (const key of REPAIR_ORDER) {
    hints[key] = applyRepairs(headers, rows, columns, repairsOnly(key)).counts[key];
  }
  return hints;
}

function repairsOnly(key: RepairKey): RepairConfig {
  return {
    trimWhitespace: key === 'trimWhitespace',
    standardizeNull: key === 'standardizeNull',
    stripCurrency: key === 'stripCurrency',
    normalizeDates: key === 'normalizeDates',
    normalizeHeaders: key === 'normalizeHeaders',
  };
}
